import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Input,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Toast,
  ToastHeader,
  ToastBody,
  Button,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { getAllGuardLocations } from "../../store/actions";
import { createSelector } from "reselect";
import { formatDate } from "../../helpers/formatDate";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  createCustomIcon,
  createVehicleIcon,
  groupNearbyLocations,
} from "../../components/Common/Map/helper";
import GuardLocationTable from "./gaurdVehicleLocationList";
import ErrorAlert from "../../components/Common/Error/ErrorAlert";
import { Link } from "react-router-dom";

// Custom component to focus map on selected entity
const MapFocus = ({ selectedEntity, allLocations, shouldFitBounds }) => {
  const map = useMap();
  const initialFitDoneRef = useRef(false);
  const previousZoomRef = useRef(null);

  // Only fit bounds to all locations on initial load
  useEffect(() => {
    if (allLocations?.length > 0 && !initialFitDoneRef.current && shouldFitBounds) {
      const bounds = allLocations
        .filter((loc) => loc.currentLocation)
        .map((loc) => [
          loc.currentLocation.latitude,
          loc.currentLocation.longitude,
        ]);

      if (bounds.length > 0) {
        setTimeout(() => {
          map.fitBounds(bounds);
          initialFitDoneRef.current = true;
          previousZoomRef.current = map.getZoom();
        }, 200); // Delay to ensure map is ready
      }
    }
  }, [allLocations, map, shouldFitBounds]);

  // This effect only responds to changes in selectedEntity
  useEffect(() => {
    if (selectedEntity && selectedEntity.currentLocation) {
      const lat = selectedEntity.currentLocation.latitude;
      const lng = selectedEntity.currentLocation.longitude;

      if (
        isValidCoordinate(lat) && 
        isValidCoordinate(lng)
      ) {
        map.flyTo([lat, lng], 18);
        previousZoomRef.current = 15;
      }
    }
  }, [selectedEntity, map]); // Only selectedEntity and map in dependencies

  // Store the current zoom level when it changes
  useEffect(() => {
    const onZoomEnd = () => {
      previousZoomRef.current = map.getZoom();
    };

    map.on('zoomend', onZoomEnd);
    return () => {
      map.off('zoomend', onZoomEnd);
    };
  }, [map]);

  return null;
};

// Function to validate coordinates
const isValidCoordinate = (coord) => {
  return coord !== undefined && coord !== null && !isNaN(coord);
};

export default function ViewAllGuardLocation() {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("guards");
  // State for selected date (default: today)
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0]; // Format YYYY-MM-DD
  });
  // New state to track if date filter is active
  const [isDateFilterActive, setIsDateFilterActive] = useState(false);
  // New state for selected guard or vehicle
  const [selectedEntity, setSelectedEntity] = useState(null);
  // Toast state for invalid coordinates
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  // Ref to track if map should re-focus
  const refreshTimerRef = useRef(null);
  // Flag to control initial bounds fitting
  const [shouldFitBounds, setShouldFitBounds] = useState(true);

  useEffect(() => {
    dispatch(getAllGuardLocations());
    
    // Set up the interval for data refresh
    refreshTimerRef.current = setInterval(() => {
      // When refreshing data, don't refit bounds
      setShouldFitBounds(false);
      dispatch(getAllGuardLocations());
    }, 60000);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [dispatch]);

  const locationsSelector = createSelector(
    (state) => state.Common,
    (Common) => Common.location
  );
  const locations = useSelector(locationsSelector);

  // Function to validate location data
  const validateLocationData = (location) => {
    if (!location) return false;

    const hasValidCoordinates =
      isValidCoordinate(location.latitude) &&
      isValidCoordinate(location.longitude);

    if (!hasValidCoordinates) {
      // Only show toast for selected entity with invalid coordinates
      if (
        selectedEntity &&
        ((selectedEntity.vehicleNumber &&
          location.vehicleNumber === selectedEntity.vehicleNumber) ||
          (!selectedEntity.vehicleNumber &&
            location.firstName === selectedEntity.firstName &&
            location.lastName === selectedEntity.lastName))
      ) {
        setToastMessage(
          `Invalid coordinates found for ${
            location.vehicleNumber
              ? `Vehicle: ${location.vehicleNumber}`
              : `Guard: ${location.firstName} ${location.lastName}`
          }`
        );
        setShowToast(true);
      }
      return false;
    }

    return true;
  };

  // Function to activate date filter when Find button is clicked
  const handleFindClick = () => {
    setIsDateFilterActive(true);
    setShouldFitBounds(true);
  };

  // Get the previous date from the selected date (for date filtering when active)
  const previousDate = new Date(selectedDate);
  previousDate.setDate(previousDate.getDate() - 1);
  const previousDateString = previousDate.toISOString().split("T")[0];

  // Filter function that applies date filtering only when active
  const filterByDate = (entity) => {
    // Skip date filtering if not active
    if (!isDateFilterActive) return true;

    if (!entity?.currentLocation?.updatedAt) return false;

    const updatedAtDate = new Date(entity.currentLocation.updatedAt)
      .toISOString()
      .split("T")[0];

    return (
      updatedAtDate === selectedDate || updatedAtDate === previousDateString
    ); // Include both dates
  };

  // Apply filters
  const filteredGuardLocations = (locations?.usersWithLocation || [])
    .filter(filterByDate)
    .filter((guard) => validateLocationData(guard.currentLocation));

  const filteredVehicleLocations = (locations?.vehicleWithLocation || [])
    .filter(filterByDate)
    .filter((vehicle) => validateLocationData(vehicle.currentLocation));

  const allLocation = [...filteredGuardLocations, ...filteredVehicleLocations];

  // Handle entity selection from the table
  const handleEntitySelection = (entity) => {
    setSelectedEntity(entity);
    setShowToast(false);

    // Check if the entity has valid coordinates
    if (entity.currentLocation) {
      const hasValidCoords =
        isValidCoordinate(entity.currentLocation.latitude) &&
        isValidCoordinate(entity.currentLocation.longitude);

      if (!hasValidCoords) {
        setToastMessage(
          `Invalid coordinates found for ${
            entity.vehicleNumber
              ? `Vehicle: ${entity.vehicleNumber}`
              : `Guard: ${entity.firstName} ${entity.lastName}`
          }`
        );
        setShowToast(true);
      }
    }
  };
  
  const groupedGuardMarkers = groupNearbyLocations(filteredGuardLocations);
  const groupedVehicleMarkers = groupNearbyLocations(filteredVehicleLocations);


  const adjustedVehicleMarkers = groupedVehicleMarkers.map(vehicleGroup => {
    const overlappingGuardGroup = groupedGuardMarkers.find(
      guardGroup => 
        Math.abs(guardGroup.lat - vehicleGroup.lat) < 0.0002 && 
        Math.abs(guardGroup.lng - vehicleGroup.lng) < 0.0002
    );
    
    if (overlappingGuardGroup) {
      
      const offset = 0.001;
      return {
        ...vehicleGroup,
        // lat: vehicleGroup.lat + offset,
        lng: vehicleGroup.lng + offset,
        isOffset: true 
      };
    }
    
    return vehicleGroup;
  });

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Control Room"
            breadcrumbItem="View Force Locations"
          />

          {/* Toast for invalid coordinates */}
          <div
            className="position-fixed top-0 end-0 p-3"
            style={{ zIndex: 1050 }}
          >
            <Toast isOpen={showToast} fade>
              <ToastHeader icon="warning" toggle={() => setShowToast(false)}>
                Coordinate Error
              </ToastHeader>
              <ToastBody>{toastMessage}</ToastBody>
            </Toast>
          </div>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row>
                    <Col md={3}>
                      <Nav tabs>
                        <NavItem>
                          <NavLink
                            style={{
                              cursor: "pointer",
                              fontWeight:
                                activeTab === "guards" ? "bold" : "normal",
                              borderBottom:
                                activeTab === "guards"
                                  ? "2px solid blue"
                                  : "none",
                            }}
                            onClick={() => {
                              setActiveTab("guards");
                              setSelectedEntity(null); // Clear selection when switching tabs
                              setShowToast(false);
                            }}
                          >
                            Guard
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            style={{
                              cursor: "pointer",
                              fontWeight:
                                activeTab === "vehicles" ? "bold" : "normal",
                              borderBottom:
                                activeTab === "vehicles"
                                  ? "2px solid blue"
                                  : "none",
                            }}
                            onClick={() => {
                              setActiveTab("vehicles");
                              setSelectedEntity(null); // Clear selection when switching tabs
                              setShowToast(false);
                            }}
                          >
                            Vehicle
                          </NavLink>
                        </NavItem>
                      </Nav>
                      <TabContent
                        activeTab={activeTab}
                        className="live-location"
                      >
                        <TabPane tabId="guards">
                          <GuardLocationTable
                            locations={locations?.usersWithLocation}
                            onEntitySelect={handleEntitySelection}
                            validateLocationData={validateLocationData}
                            cursorPointer={true} // Add cursor pointer
                          />
                        </TabPane>
                        <TabPane tabId="vehicles">
                          <GuardLocationTable
                            locations={locations?.vehicleWithLocation}
                            onEntitySelect={handleEntitySelection}
                            validateLocationData={validateLocationData}
                            cursorPointer={true} // Add cursor pointer
                          />
                        </TabPane>
                      </TabContent>
                    </Col>

                    <Col md={9}>
                      <Row className="justify-content-center mb-3">
                        <Col md={5} className="d-flex align-items-end">
                          <Input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => {
                              setSelectedDate(e.target.value);
                              // Don't apply filter immediately, wait for button click
                            }}
                            className="me-2"
                          />
                          <Button 
                            color="primary" 
                            onClick={handleFindClick}
                          >
                            Find
                          </Button>
                        </Col>
                        <Col md={2} className="text-center">
                          <Link to="/all-location"><Button color="primary" className="mb-0">More details</Button></Link>
                        </Col>
                        <Col md={7}>
                          {isDateFilterActive && (
                            <div className="text-muted text-end">
                              <small>
                                Filtering data for: {selectedDate}
                                <Button 
                                  color="link" 
                                  size="sm" 
                                  className="p-0 ms-2"
                                  onClick={() => setIsDateFilterActive(false)}
                                >
                                  Clear Filter
                                </Button>
                              </small>
                            </div>
                          )}
                        </Col>
                      </Row>

                      {selectedEntity && (
                        <Row>
                          <Col>
                            {selectedEntity &&
                              !validateLocationData(
                                selectedEntity.currentLocation
                              ) && (
                                <div className="mb-3">
                                  <ErrorAlert
                                    error={`No location data available for ${
                                      selectedEntity.vehicleNumber 
                                        ? selectedEntity.vehicleNumber
                                        : `${selectedEntity.firstName} ${selectedEntity.lastName}`
                                    }`}
                                  />
                                </div>
                              )}
                          </Col>
                        </Row>
                      )}

                      <div style={{ height: "80vh", width: "100%" }}>
                        <MapContainer
                          key="guard-location-map" // Stable key prevents remounts
                          center={[20.5937, 78.9629]}
                          zoom={5}
                          style={{ height: "90%", width: "100%" }}
                        >
<TileLayer
                            url="http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
                            subdomains={["mt0", "mt1", "mt2", "mt3"]}
                          />
                          <MapFocus
                            selectedEntity={selectedEntity}
                            allLocations={allLocation}
                            shouldFitBounds={shouldFitBounds}
                          />
                          {activeTab!=="vehicles" && [
                            ...groupedGuardMarkers
                          ].map((group, index) => (
                            <Marker
                              key={`marker-${group.lat}-${group.lng}-${index}`}
                              position={[group.lat, group.lng]}
                              icon={
                                group.entities.some((e) => e.vehicleNumber)
                                  ? createVehicleIcon()
                                  : createCustomIcon(
                                      null,
                                      group.entities[0]?.currentLocation
                                        ?.updatedAt,
                                      true
                                    )
                              }
                            >
                              <Popup>
                                {group.entities.map((entity, i) => (
                                  <div
                                    key={`popup-${i}`}
                                    style={{ marginBottom: "0.5rem" }}
                                  >
                                    <strong>
                                      {entity.vehicleNumber
                                        ? `🚗 ${entity.vehicleNumber}`
                                        : `${entity.firstName} ${entity.lastName}`}
                                    </strong>
                                    <br />
                                    📞{" "}
                                    <a
                                      href={`tel:${entity.contactNumber}`}
                                      style={{ color: "blue" }}
                                    >
                                      {entity.contactNumber}
                                    </a>
                                    <br />
                                    🌍 {" "}
                                    {entity.currentLocation.latitude},{" "}
                                  
                                    {entity.currentLocation.longitude}
                                    <br/>
    📡 <strong>Location on:</strong>{" "}
    {entity.currentLocation.isLocationOn ? "Yes" : "No"}
                                    <br />⏰ <strong>Last Update:</strong>{" "}
                                    {formatDate(
                                      entity.currentLocation?.updatedAt
                                    )}
                                  </div>
                                ))}
                              </Popup>
                            </Marker>
                          ))}
                          

                           {/* Then render vehicle markers with possible offset */}
      {activeTab=="vehicles" && adjustedVehicleMarkers?.map((group, index) => (
        <Marker
          key={`vehicle-marker-${group.lat}-${group.lng}-${index}`}
          position={[group.lat, group.lng]}
          icon={createVehicleIcon()}
        >
          <Popup>
            
            {group.entities.map((entity, i) => (
              <div
                key={`popup-${i}`}
                style={{ marginBottom: "0.5rem" }}
              >
                <strong>
                  {`🚗 ${entity.vehicleNumber}`}
                </strong>
                <br />
                📞{" "}
                <a
                  href={`tel:${entity.contactNumber}`}
                  style={{ color: "blue" }}
                >
                  {entity.contactNumber}
                </a>
                <br />
                🌍 {" "}
                {entity.currentLocation.latitude},
                {entity.currentLocation.longitude}
                                    <br/>
    📡 <strong>Location on:</strong>{" "}
    {entity?.currentLocation?.isLocationOn ? "Yes" : "No"}
                <br />⏰ <strong>Last Update:</strong>{" "}
                {formatDate(
                  entity.currentLocation?.updatedAt
                )}
                  </div>
            ))}
          </Popup>
          </Marker>
                          ))}
                        </MapContainer>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}